import { CircleChevron } from 'app/assets/icons'
import { style } from 'app/assets/styles'
import { Link } from 'app/common/link'
import CarouselElement from 'nuka-carousel'
import React, { Component } from 'react'
import { injectIntl } from 'react-intl'
import { colors } from 'shared/assets/styles'
import { eventsDefinitions } from 'shared/store/metadata'
import styled from 'styled-components'
import type { MarketingAnimationShortDTO } from 'shared/types'
import type { IntlShape } from 'react-intl'
import { media } from 'app/assets/styles'
import { Picture } from 'app/common/picture'
import { contentFormat } from 'shared/utils'
import { pageCategoryMapping } from 'shared/store/metadata'
import ReactPlayer from 'react-player'

const { red, lightGrey } = colors

type Props = {
    primary: Array<MarketingAnimationShortDTO>,
    intl: IntlShape
}

type State = {
    width: number,
    height: number,
    slideIndex: number
}

class Hero extends Component<Props, State> {
    constructor(props) {
        super(props)
        this.state = { width: 0, height: 0, slideIndex: 0 }
    }

    componentDidMount() {
        this.updateWindowDimensions()
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    updateWindowDimensions = () => {
        this.setState({ width: window.innerWidth, height: window.innerHeight })
    }

    render() {
        const { primary, intl } = this.props
        const { slideIndex, width } = this.state
        const isPortrait = width < 993

        const primaryRender = primary.map((item, index) => {
            const {
                actionLabel,
                imageUrlLandscape,
                imageUrlPortrait,
                imageUrlCropped,
                enableVideo,
                audio,
                autoPlay,
                repeat,
                videoUrlLandscape,
                videoUrlPortrait,
                imageTitle,
                actionColor,
                external,
                actionUrl,
                actionEnabled
            } = item

            const pictures = {
                imageUrlLandscape,
                imageUrlPortrait,
                imageUrlCropped,
                imageTitle
            }
            const meta = [
                {
                    ...eventsDefinitions['PRIMARY'],
                    event_label: actionUrl ? actionUrl.split('/')[1] : '',
                    event_action: actionLabel
                },
                {
                    ...eventsDefinitions.GA4_CONTENT_ACCESS,
                    ...pageCategoryMapping(intl),
                    cta_name: actionLabel,
                    operation_name: (actionUrl || '').split('/').pop(),
                    content_format: contentFormat.SLIDER
                }
            ]
            const cta = actionEnabled ? (
                <StyledLink
                    url={actionUrl}
                    meta={meta}
                    external={external}
                    color="white"
                    background={actionColor}>
                    {actionLabel}
                </StyledLink>
            ) : null

            return (
                <ItemContainer
                    key={index}
                    url={actionUrl}
                    meta={meta}
                    external={external}>
                    {enableVideo && index === slideIndex ? (
                        <ReactPlayer
                            wrapper={PlayerWrapper}
                            width={'100%'}
                            playsinline
                            muted={autoPlay || !audio}
                            playing={autoPlay}
                            loop={repeat}
                            key={
                                isPortrait
                                    ? videoUrlPortrait
                                    : videoUrlLandscape
                            }
                            height={
                                this.state.width / (isPortrait ? 2 / 3 : 16 / 7)
                            }
                            controls={false}
                            config={{
                                file: {
                                    attributes: {
                                        poster: isPortrait
                                            ? pictures.imageUrlPortrait[0]
                                            : pictures.imageUrlLandscape[0]
                                    }
                                }
                            }}
                            url={
                                isPortrait
                                    ? videoUrlPortrait
                                    : videoUrlLandscape
                            }
                        />
                    ) : (
                        <SafariPictureWrapper>
                            <Picture
                                {...pictures}
                                desktopRatio="16/7"
                                mobileRatio="2/3"
                                mode="cover"
                            />
                        </SafariPictureWrapper>
                    )}
                    <LinkContainer>{cta}</LinkContainer>
                </ItemContainer>
            )
        })

        const margin = width > 1280 ? (width - 1280) / 2 : 20
        const carousel = primary.length > 1
        return (
            <HeroContainer>
                {carousel ? (
                    <Carousel
                        slideIndex={slideIndex}
                        afterSlide={slideIndex => this.setState({ slideIndex })}
                        wrapAround
                        autoplay
                        autoplayInterval={10000}
                        defaultControlsConfig={{
                            pagingDotsStyle: {
                                fill: lightGrey
                            }
                        }}
                        renderCenterLeftControls={({ previousSlide }) => (
                            <ChevronContainer
                                onClick={previousSlide}
                                margin={margin}>
                                <CircleChevron
                                    className="carouselChevron"
                                    direction="left"
                                    background="white"
                                />
                            </ChevronContainer>
                        )}
                        renderCenterRightControls={({ nextSlide }) => (
                            <ChevronContainer
                                onClick={nextSlide}
                                margin={margin}>
                                <CircleChevron
                                    className="carouselChevron"
                                    background="white"
                                />
                            </ChevronContainer>
                        )}>
                        {primaryRender}
                    </Carousel>
                ) : (
                    <PictureContainer>{primaryRender}</PictureContainer>
                )}
            </HeroContainer>
        )
    }
}
const ItemContainer = styled(Link)`
    cursor: pointer;
    display: block;
    width: 100%;
    /* Fix for Safari image display in carousel */
    -webkit-transform: translateZ(0);
    transform: translateZ(0);

    img {
        width: 100%;
        height: auto;
        display: block;
        /* Safari-specific fixes */
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        -webkit-transform: translate3d(0, 0, 0);
        transform: translate3d(0, 0, 0);
    }

    picture {
        width: 100%;
        display: block;

        img {
            width: 100%;
            height: auto;
            object-fit: cover;
        }
    }
`

const Carousel = styled(CarouselElement)`
    padding-bottom: 60px;
    margin-bottom: -10px;

    /* Safari carousel fixes */
    -webkit-transform: translateZ(0);
    transform: translateZ(0);

    .slider-list {
        /* Ensure proper display in Safari */
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        -webkit-perspective: 1000px;
        perspective: 1000px;
    }

    .slider-slide {
        /* Safari slide fixes */
        -webkit-transform: translateZ(0);
        transform: translateZ(0);
        display: block !important;
        width: 100% !important;

        /* Ensure images are properly contained */
        overflow: hidden;

        img,
        picture {
            width: 100%;
            height: auto;
            display: block;
            max-width: 100%;
        }
    }

    ${media.medium`
        margin-bottom: 0;
    `}
    ${props => `
        .slider-control-bottomcenter li:nth-child(${props.slideIndex +
            1}) button {
            fill: ${red} !important;
        }`};

    .paging-dot {
        width: 18px;
        height: 18px;
    }
    .paging-dot > circle {
        r: 5px;
        cx: 10px;
        cy: 10px;
    }
    &:focus {
        outline: none; /* Removes the blue border */
    }
`

const LinkContainer = styled.div`
    text-align: center;
    position: relative;
    left: 50%;
    transform: translate(-50%, -60px);
    height: 0;
`
const StyledLink = styled(Link)`
    ${style.buttonMedium};
    border: none;
`

const PlayerWrapper = styled.div`
    video {
        object-fit: cover;
    }
`

const HeroContainer = styled.div`
    font-size: 0;
`

const ChevronContainer = styled.div`
    cursor: pointer;
    ${props => `
        margin-left: ${props.margin}px;
        margin-right: ${props.margin}px;
    `};
`

const PictureContainer = styled.div`
    padding-bottom: 30px;
    ${media.medium`
        padding-bottom: 40px;
    `}
`

const SafariPictureWrapper = styled.div`
    /* Safari-specific wrapper for Picture component */
    width: 100%;
    display: block;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;

    /* Ensure proper image sizing in Safari */
    img,
    picture {
        width: 100%;
        height: auto;
        display: block;
        object-fit: cover;
        max-width: 100%;
    }

    /* Fix for TwicPics images in Safari */
    .twic-img {
        width: 100% !important;
        height: auto !important;
        display: block !important;
    }

    /* Fix for responsive picture component */
    picture {
        width: 100%;
        height: auto;

        source,
        img {
            width: 100%;
            height: auto;
            display: block;
        }
    }
`

const HeroWithIntl = injectIntl(Hero)

export { HeroWithIntl as Hero }
